import { Point } from '@antv/g-base';
import { ItemType } from '../../constants';
import {
  DrawOpts,
  EdgeModel,
  Group,
  NodeModel,
  ShapeAttrs,
  ShapeElement,
  ShapeSize,
} from '../../types';
import { shapeNames } from '../constants';

const getDeleteCoordinatesForEdge = ({ group }: { group: Group }): Point => {
  const shape = group.get('children')[0];
  return shape.getPoint(0.5);
};

const getDeleteCoordinatesForNode = ({
  size,
  deleteStyle,
}: {
  size: ShapeSize;
  deleteStyle: Record<string, any>;
}): Point => {
  const [wrapperWidth] = size;
  const { height = 0, r = 0 } = deleteStyle || {};
  return { x: wrapperWidth, y: 0 - height / 2 + r };
};

interface DeleteAttrsArgsBase {
  deleteStyle: Record<string, any>;
  deleteIconStyle: Record<string, any>;
  type: ItemType;
  group: Group;
}

interface NodeDeleteAttrsArgs extends DeleteAttrsArgsBase {
  size: ShapeSize;
}

type EdgeDeleteAttrsArgs = DeleteAttrsArgsBase;

interface DeleteAttrsReturn {
  hitArea: ShapeAttrs;
  circle: ShapeAttrs;
  icon: ShapeAttrs;
}

const isNodeDeleteAttrs = (
  args: EdgeDeleteAttrsArgs | NodeDeleteAttrsArgs,
): args is NodeDeleteAttrsArgs => args.type === ItemType.Node;

function getDeleteAttrs(args: NodeDeleteAttrsArgs): DeleteAttrsReturn;
function getDeleteAttrs(args: EdgeDeleteAttrsArgs): DeleteAttrsReturn;
function getDeleteAttrs(args: NodeDeleteAttrsArgs | EdgeDeleteAttrsArgs): DeleteAttrsReturn {
  const { deleteStyle, deleteIconStyle, group } = args;
  const basePosition = isNodeDeleteAttrs(args)
    ? getDeleteCoordinatesForNode({ size: args.size, deleteStyle })
    : getDeleteCoordinatesForEdge({ group });
  // Allows for the delete icon to show while still slightly out of bounds.
  // Helpful in terms of usability as it makes the target easier to hit
  const HIT_OFFSET = 2;

  return {
    hitArea: {
      x: basePosition.x - HIT_OFFSET,
      y: basePosition.y - HIT_OFFSET,
      width: deleteStyle.width + HIT_OFFSET * 2,
      height: deleteStyle.height + HIT_OFFSET * 2,
      r: deleteStyle.r + HIT_OFFSET,
      fill: 'transparent',
      cursor: 'pointer',
      // make sure this is always on top of other attrs
      zIndex: 999,
    },
    circle: { ...basePosition, ...deleteStyle, baseFill: deleteStyle.fill },
    icon: {
      x: basePosition.x - deleteIconStyle.width / 2,
      y: basePosition.y - deleteIconStyle.height / 2,
      ...deleteIconStyle,
    },
  };
}

interface NodeDrawOptions extends DrawOpts {
  // Type of container on which the node is drawn
  type?: ItemType;
}

type EdgeDrawOptions = Omit<NodeDrawOptions, 'size'>;

/**
 * Draws the delete button
 * NOTE: We are no longer using the delete group because it stops us from being
 * able to use the quick state updates on edges for some reason
 */
export function drawDelete(args: NodeDrawOptions): void;
export function drawDelete(args: EdgeDrawOptions): void;

export function drawDelete(args: NodeDrawOptions | EdgeDrawOptions) {
  const { options, group, type = ItemType.Node, ...rest } = args;
  const { circle, icon, hitArea } = getDeleteAttrs({
    deleteIconStyle: options[shapeNames.deleteIcon],
    deleteStyle: options[shapeNames.deleteCircle],
    group,
    type,
    ...rest,
  });

  // draws the circle
  group.addShape('circle', {
    name: shapeNames.deleteCircle,
    className: shapeNames.deleteCircle,
    attrs: circle,
  });

  // draws the 'x'
  group.addShape('image', {
    name: shapeNames.deleteIcon,
    className: shapeNames.deleteIcon,
    attrs: icon,
  });

  // This creates an area which will always be triggered when the cursor is
  // near it, allowing for easy clicking
  // Since it is transparent (instead of an opacity of zero), it will also be
  // triggered when hovering over the empty delete space.  This solves the
  // issue in which the delete icon sometimes disappears when hovering over it
  // in certain browsers
  group.addShape('circle', {
    name: shapeNames.delete,
    className: shapeNames.delete,
    draggable: true,
    capture: true,
    attrs: hitArea,
  });

  // Make sure shapes appear in the correct z-index order
  group.sort();

  return group;
}

const getDeleteShapes = (group: Group) =>
  [shapeNames.deleteCircle, shapeNames.deleteIcon, shapeNames.delete].map((s) =>
    group.findByClassName(s),
  );

interface DeleteVisibilityArgs {
  group: Group;
  isVisible: boolean;
}

/**
 * Changes the delete visibility in a way that is easily overridable by state styles
 * Use this if you want to change delete visibility in a way which will be
 * overridden by the next state update (eg: active change)
 */
export const updateDeleteOpacity = ({ group, isVisible = true }: DeleteVisibilityArgs) => {
  const updatedAttrs = { opacity: isVisible ? 1 : 0 };
  getDeleteShapes(group).forEach((shape) => shape.attr(updatedAttrs));
};

/**
 * Sets the delete icon to either be visible or hidden
 * Setting this will not override it when state style changes until you manually
 * change it back to visible
 * Use this for a more persistent way to hide/show the delete button.
 */
export const updateDeleteVisibility = ({ group, isVisible = true }: DeleteVisibilityArgs) =>
  getDeleteShapes(group).forEach((shape) => {
    // if it's already in a visible state, ignore this
    if (!shape || shape.get('visible') === isVisible) return;
    // eslint-disable-next-line no-unused-expressions
    isVisible ? shape.show() : shape.hide();
  });

/**
 * Moves the delete icon to the correct position
 * Needs to be run whenever the node width changes
 */
export function updateDeletePosition(args: NodeDrawOptions): void;
export function updateDeletePosition(args: EdgeDrawOptions): void;

export function updateDeletePosition(args: NodeDrawOptions | EdgeDrawOptions) {
  const { options, group, type = ItemType.Node, ...rest } = args;
  const { circle, icon, hitArea } = getDeleteAttrs({
    deleteIconStyle: options[shapeNames.deleteIcon],
    deleteStyle: options[shapeNames.deleteCircle],
    group,
    type,
    ...rest,
  });

  group.findByClassName(shapeNames.deleteCircle).attr(circle);
  group.findByClassName(shapeNames.deleteIcon).attr(icon);
  group.findByClassName(shapeNames.delete).attr(hitArea);
}

/**
 * Give the delete button the hover styling
 */
const setHoverFill = (group: Group) => {
  const deleteCircle = group.findByClassName(shapeNames.deleteCircle) as ShapeElement;
  deleteCircle.attr({ fill: deleteCircle.attrs.hoverFill });
};

/**
 * Give the delete button the base styling
 */
const setBaseFill = (group: Group) => {
  const deleteCircle = group.findByClassName(shapeNames.deleteCircle) as ShapeElement;
  deleteCircle.attr({ fill: deleteCircle.attrs.baseFill });
};

export const isDeleteEnabled = (model: NodeModel | EdgeModel): boolean =>
  model?.canDelete !== false;

export default {
  draw: drawDelete,
  isEnabled: isDeleteEnabled,
  setBaseFill,
  setHoverFill,
  updatePosition: updateDeletePosition,
  updateVisibility: updateDeleteVisibility,
  updateOpacity: updateDeleteOpacity,
};
